import { useShallow } from 'zustand/react/shallow';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
// mantine
import { Select } from '@mantine/core';
// components
import { IConnector, ISettingPreset } from '@newo/api/gateway';
import { getVoiceOptions } from '../../Voice.utils';
import VoiceSelect from '@/pages/Settings/components/Voice/components/VoiceSelect';
// utils
import { useVoices } from '@/services/voices/voices.queries';
import useSettingsStore from '@/pages/Settings/Settings.store';
import {
    dictionaryToSettings,
    settingsToDictionary
} from '@/pages/Settings/Settings.utils';
import { cn } from '@/utils';

interface VoiceFormProps {
    connector: IConnector;
    settingsDescription: Record<string, ISettingPreset>;
    schema: Record<string, unknown>;
}

const VoiceForm = ({
    connector,
    settingsDescription,
    schema
}: VoiceFormProps) => {
    const { setVoiceValues, setVoiceErrors } = useSettingsStore(
        useShallow((state) => ({
            setVoiceValues: state.setVoiceValues,
            setVoiceErrors: state.setVoiceErrors
        }))
    );

    const { data: voices = [], isLoading } = useVoices(connector.connector_idn);

    const pendingSettings = useSettingsStore(
        (state) => state.voiceValues[connector.id]
    );

    const settings = useMemo<Record<string, string>>(() => {
        return settingsToDictionary(connector.settings, settingsDescription);
    }, [connector]);

    const formik = useFormik({
        initialValues: {
            ...settings,
            ...settingsToDictionary(pendingSettings, settingsDescription)
        },
        validationSchema: schema,
        onSubmit: () => {
            /** keep empty */
        },
        validateOnChange: true,
        validateOnBlur: true,
        enableReinitialize: true
    });

    const handleValueChange = useCallback(() => {
        if (formik.isValid && formik.dirty) {
            setVoiceValues({
                [connector.id]: dictionaryToSettings(formik.values)
            });
        }
    }, [
        formik.isValid,
        formik.dirty,
        formik.values,
        connector.id,
        setVoiceValues
    ]);

    const handleErrorChange = useCallback(() => {
        setVoiceErrors({
            [connector.id]: formik.isValid && formik.dirty
        });
    }, [formik.errors.value, connector.id, setVoiceErrors]);

    const handleVoiceChange = useCallback((voiceId: string) => {
        formik.setFieldValue(
            'elevenlabs_tts_voice_id',
            formik.values.elevenlabs_tts_voice_id === voiceId
                ? undefined
                : voiceId
        );
    }, []);

    useEffect(() => {
        handleValueChange();
    }, [handleValueChange]);

    useEffect(() => {
        handleErrorChange();
    }, [handleErrorChange]);

    const voiceOptions = useMemo(() => {
        return getVoiceOptions(voices, 'en');
    }, [voices, formik.values.elevenlabs_tts_language_code]);

    const handleLanguageChange = useCallback((value: string | null) => {
        formik.setFieldValue('elevenlabs_tts_language_code', value);
    }, []);

    return (
        <article className={cn(isLoading && 'opacity-50 blur-[2px]')}>
            <div className="font-semibold mb-1">{connector.connector_idn}</div>
            <div className="flex text-sm flex-col gap-4 relative rounded-lg bg-[#FAFAFA] p-4 border-1 border-solid border-[#DEE2E6]/50">
                <Select
                    data={
                        settingsDescription.elevenlabs_tts_language_code
                            .possible_values as string[]
                    }
                    description={
                        settingsDescription.elevenlabs_tts_language_code
                            .description
                    }
                    error={formik.errors.elevenlabs_tts_language_code}
                    label={
                        settingsDescription.elevenlabs_tts_language_code.title
                    }
                    name="elevenlabs_tts_language_code"
                    value={formik.values.elevenlabs_tts_language_code}
                    onChange={handleLanguageChange}
                />

                <VoiceSelect
                    options={voiceOptions}
                    voiceId={formik.values.elevenlabs_tts_voice_id}
                    onChange={handleVoiceChange}
                />
            </div>
        </article>
    );
};

export default VoiceForm;
